// React & Hooks
import { memo, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";

// API & Forms
import {
  createFleetWarehouseAPI,
  getFleetWarehouseAPI,
  updateFleetWarehouseAPI,
} from "services/api/fleet/warehouses/api";
import { initialFleetWarehouseEditForm } from "services/api/fleet/warehouses/consts";
import { useHandleTextFieldChange } from "services/forms/hooks";

// Types & Validation
import { FleetWarehouseEditFormValues } from "services/api/fleet/warehouses/types";
import { Errors } from "services/forms/validation/types";
import { WarehouseEditProps } from "./types";
import { WAREHOUSE_EDIT_FORM_SCHEMA } from "./validation";

// UI Komponente
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";

// Lokalne komponente
import WarehouseAddressTab from "./components/WarehouseAddressTab/WarehouseAddressTab";
import WarehouseContactTab from "./components/WarehouseContactTab/WarehouseContactTab";
import WarehouseIntro from "./components/WarehouseIntro/WarehouseIntro";
import WarehouseWorkingHoursStep from "./components/WarehouseWorkingHoursStep/WarehouseWorkingHoursStep";
import { isInvalidWorkingDay } from "./components/WarehouseWorkingHoursStep/consts";

const WORKING_HOURS_STEP = 2;

/** Čarobnjak skladišta: osnovni podaci, adresa, radno vrijeme i kontakti. Sprema se na kraju. */
const WarehouseEditCard: React.FC<WarehouseEditProps> = (props) => {
  const { warehouseId, show, onClose, onSuccess } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);

  const [formValues, setFormValues] = useState<FleetWarehouseEditFormValues>(
    initialFleetWarehouseEditForm
  );

  useEffect(() => {
    if (!show) return;

    setStepCurrent(0);
    setErrors({});

    if (warehouseId) {
      fetchWarehouse(warehouseId);
    } else {
      setFormValues(initialFleetWarehouseEditForm);
    }
  }, [show, warehouseId]);

  const fetchWarehouse = async (id: string) => {
    setIsLoading(true);

    let result;
    try {
      result = await getFleetWarehouseAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const handleNextStep = () => {
    if (stepCurrent === 0) {
      const isValid = validateYup(formValues, WAREHOUSE_EDIT_FORM_SCHEMA, setErrors);
      if (!isValid) return;
    }

    // Neispravno vrijeme se vidi uz dan, pa se samo ne pušta dalje.
    if (stepCurrent === WORKING_HOURS_STEP && formValues.workingHours.some(isInvalidWorkingDay)) return;

    setStepCurrent((step) => step + 1);
  };

  const handleBackStep = () => {
    setStepCurrent((step) => step - 1);
  };

  const handleFinish = async () => {
    const isValid = validateYup(formValues, WAREHOUSE_EDIT_FORM_SCHEMA, setErrors);

    if (!isValid) {
      setStepCurrent(0);
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!warehouseId) {
        result = await updateFleetWarehouseAPI(warehouseId, formValues);
      } else {
        result = await createFleetWarehouseAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const steps = [
    <WizardStep key="info" stepNumber={0} stepCurrent={stepCurrent}>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Osnovni podaci</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Upišite naziv skladišta i kratak opis, npr. što se u njemu drži i kako se do njega dolazi.
          </p>
          <div className="px-3 space-y-3">
            <div>
              <TextField
                type="text"
                name="name"
                label={t("admin.fleet.warehouse.add.name")}
                placeholder={t("admin.fleet.warehouse.add.name.placeholder")}
                onChange={handleTextFieldChange}
                error={errors.name}
                value={formValues.name}
                required
                endIcon=""
              />
            </div>
            <div>
              <TextArea
                name="description"
                label="Opis"
                placeholder="Unesite opis skladišta"
                onChange={handleTextFieldChange}
                value={formValues.description}
                rows={5}
              />
            </div>
          </div>
        </div>
      </div>
    </WizardStep>,
    // Korak adrese nosi vlastiti naslov i opis, kao adrese u čarobnjaku kontakta.
    <WizardStep key="address" stepNumber={1} stepCurrent={stepCurrent}>
      <WarehouseAddressTab
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </WizardStep>,
    // Radno vrijeme po danima, s predlošcima za cijeli tjedan.
    <WizardStep key="workingHours" stepNumber={WORKING_HOURS_STEP} stepCurrent={stepCurrent}>
      <WarehouseWorkingHoursStep
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </WizardStep>,
    // Korak kontakata nosi vlastiti naslov i opis, kao predstavnici u čarobnjaku kontakta.
    <WizardStep key="contacts" stepNumber={3} stepCurrent={stepCurrent}>
      <WarehouseContactTab
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      position="side"
      size="full"
      intro={{
        content: (
          <WarehouseIntro
            formValues={formValues}
            stepCurrent={stepCurrent}
            stepCount={steps.length}
          />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={warehouseId ? "Ažuriranje skladišta" : "Kreiranje skladišta"}
        onHide={onClose}
      />
      {/* Podnožje je pravi WizardFooter, pa donji razmak za "fixed" podnožje otpada. */}
      <ModalBody fixedFooterSpacing={false}>
        <Wizard steps={steps} />
        <Loader isLoading={isLoading}> </Loader>
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};
export default memo(WarehouseEditCard);
